import * as React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Button,
    IconButton,
    Tooltip,
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkIcon from '@mui/icons-material/Link';
import {useTranslation} from "react-i18next";
import {TherapyInvite} from "./therapy-invite";

// Compact, pill-shaped action buttons tuned to fit the whole row (incl. delete)
// on a single line within the sessions card. Colors match the Figma tokens.
const pillBase = {
    borderRadius: '999px',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'none' as const,
    whiteSpace: 'nowrap' as const,
    px: 1,
    py: 0.75,
    minWidth: 'auto',
};
const startBtnSx = {...pillBase, backgroundColor: '#F2D964', color: '#0B3139', '&:hover': {backgroundColor: '#EBCF4E'}};
const outlineBtnSx = {
    ...pillBase,
    backgroundColor: 'transparent',
    color: '#134F5C',
    borderColor: '#90C6D0',
    '&:hover': {borderColor: '#134F5C', backgroundColor: '#F3FBFA'},
    '& .MuiButton-startIcon': {color: '#134F5C', mr: 0.5},
};

interface InvitesTableProps {
    rows: TherapyInvite[];
    onStart: (invite: TherapyInvite) => void;
    onCopyLink: (invite: TherapyInvite) => void;
    onShowDetails: (invite: TherapyInvite) => void;
    onEdit: (invite: TherapyInvite) => void;
    onDelete: (invite: TherapyInvite) => void;
}

const emptyValue = "—";

const headCellSx = {
    fontWeight: 500,
    color: '#535862',
    fontSize: '13px',
    py: 1,
    borderBottom: 'none',
} as const;

const InvitesTable: React.FC<InvitesTableProps> = ({rows, onStart, onCopyLink, onShowDetails, onEdit, onDelete}) => {
    const {t} = useTranslation();

    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{backgroundColor: 'transparent', boxShadow: 'none', p: 0, overflowX: 'auto'}}
        >
            <Table
                sx={{
                    minWidth: 820,
                    width: '100%',
                    borderCollapse: 'separate',
                    borderSpacing: '0 14px',
                    '& th, & td': {border: 0},
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell variant="head" sx={{...headCellSx, minWidth: 160}}>{t("Name (label)")}</TableCell>
                        <TableCell variant="head" sx={{...headCellSx, minWidth: 220}}>{t("Patient / session description")}</TableCell>
                        <TableCell variant="head" sx={{...headCellSx, whiteSpace: 'nowrap'}}>{t("Last session date")}</TableCell>
                        <TableCell variant="head" sx={headCellSx} />
                        <TableCell variant="head" sx={headCellSx} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => {
                        // Owner wants the date of the last actual session (when both
                        // sides connected), not the invite date. Backed by
                        // `lastSessionAt` — placeholder until the API supplies it.
                        const lastSession = row.lastSessionAt ? new Date(row.lastSessionAt) : null;
                        return (
                            <TableRow
                                key={row._id || index}
                                sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: '16px',
                                    '&:hover': {
                                        outline: '2px solid #CDE8FF',
                                        outlineOffset: '-2px',
                                    },
                                }}
                            >
                                <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                        borderTopLeftRadius: '16px',
                                        borderBottomLeftRadius: '16px',
                                        py: 2.5, px: 2,
                                        fontWeight: 700,
                                        color: '#0e3c47',
                                    }}
                                    data-cs-mask=""
                                >
                                    {row.inviteeName}
                                </TableCell>
                                <TableCell sx={{py: 2.5, px: 2, color: '#535862', fontSize: '13px', maxWidth: 240, minWidth: 170}} data-cs-mask="">
                                    {row.comment || <span style={{color: '#9aa4ac'}}>{emptyValue}</span>}
                                </TableCell>
                                <TableCell sx={{whiteSpace: 'nowrap', py: 2.5, px: 2, color: '#535862', fontSize: '12px'}}>
                                    {lastSession ? (
                                        <>
                                            {t("Date")}:&nbsp;<span style={{color: '#232832'}}>{lastSession.toLocaleDateString()}</span><br />
                                            {t("Time")}:&nbsp;<span style={{color: '#232832'}}>{lastSession.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                                        </>
                                    ) : (
                                        <Tooltip title={t("No session held yet")}>
                                            <span style={{color: '#9aa4ac'}}>{emptyValue}</span>
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell align="right" sx={{py: 2, px: 0.5, whiteSpace: 'nowrap'}}>
                                    <Box sx={{display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center'}}>
                                        <Button variant="contained" disableElevation sx={startBtnSx} onClick={() => onStart(row)}>
                                            {t("Start Session")}
                                        </Button>
                                        <Tooltip title={t("Copy link")}>
                                            <IconButton
                                                onClick={() => onCopyLink(row)}
                                                aria-label={t("Copy link")}
                                                sx={{color: '#134F5C', border: '1px solid #90C6D0', p: 0.75}}
                                            >
                                                <LinkIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Button variant="outlined" sx={outlineBtnSx} onClick={() => onShowDetails(row)}>
                                            {t("Invitation content")}
                                        </Button>
                                    </Box>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: '1%',
                                        whiteSpace: 'nowrap',
                                        borderTopRightRadius: '16px',
                                        borderBottomRightRadius: '16px',
                                        py: 2, px: 1,
                                    }}
                                >
                                    <Box sx={{display: 'flex', flexWrap: 'nowrap', gap: 0.5, justifyContent: 'center', alignItems: 'center'}}>
                                        <IconButton onClick={() => onEdit(row)} aria-label={t("Edit session")} sx={{color: '#134F5C'}}>
                                            <EditOutlinedIcon />
                                        </IconButton>
                                        <IconButton onClick={() => onDelete(row)} aria-label="delete" sx={{color: '#861a22'}}>
                                            <DeleteOutlinedIcon />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default InvitesTable;
